import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../design/dialog";
import { Button } from "../../design/button";
import { Subheading } from "../../design/heading";
import { InformationCircleIcon } from "@heroicons/react/20/solid";

interface Props {
  type: "supervisor" | "parent" | "case" | "child";
}

const FilesLearnMore: React.FC<Props> = ({ type }) => {
  const [open, setOpen] = useState(false);

  const handleAction = () => {
    setOpen(true);
  };

  const bodyContent = {
    supervisor: (
      <>
        <Subheading className="mb-4">How Supervisors Can Use Files:</Subheading>
        <ul className="pl-5 list-disc">
          <li>
            Upload and organize case files, such as visitation reports, legal
            documentation, and any supervisor-specific notes.
          </li>
          <li>
            Ensure files are grouped by case for seamless tracking and easy
            retrieval during visits.
          </li>
          <li>
            Control access to files, sharing essential information with case
            managers while keeping sensitive content protected.
          </li>
        </ul>
        <Subheading className="mt-4">Tip for Supervisors:</Subheading>
        Only share necessary documents with parties involved to respect
        confidentiality, but make sure supervisors have access to all critical
        instructions to enhance visitation management.
      </>
    ),
    parent: (
      <>
        <Subheading className="mb-4">How Parents Can Use Files:</Subheading>
        <ul className="pl-5 list-disc">
          <li>
            Upload important case-related files, including legal agreements,
            visitation schedules, or parenting guidelines.
          </li>
          <li>
            Organize files by case to easily reference legal documents or notes
            during custody arrangements.
          </li>
          <li>
            Decide what information to share with supervisors, ensuring they
            have the documents needed to facilitate smooth visitations.
          </li>
        </ul>
        <Subheading className="mt-4">Tip for Parents:</Subheading>
        Grant access to essential files like schedules or instructions, but
        protect private legal documents to maintain confidentiality and
        boundaries.
      </>
    ),
    case: (
      <>
        <Subheading className="mb-4">How to Manage Case Files:</Subheading>
        <ul className="pl-5 list-disc">
          <li>
            Upload and store case-specific documents, including court orders,
            legal files, and communication logs.
          </li>
          <li>
            Ensure all files are categorized by case to simplify access for
            supervisors and other relevant parties.
          </li>
          <li>
            Regulate file access to ensure that only necessary participants can
            view or modify critical documents.
          </li>
        </ul>
        <Subheading className="mt-4">Tip for Managing Case Files:</Subheading>
        Keep case files up to date and well-organized to streamline the
        documentation process and ensure all parties are informed during custody
        or legal proceedings.
      </>
    ),
    child: (
      <>
        <Subheading className="mb-4">
          How to Use Files for Child Information:
        </Subheading>
        <ul className="pl-5 list-disc">
          <li>
            Upload key documents relating to the child's care, including medical
            records, visitation notes, or educational updates.
          </li>
          <li>
            Organize files by case to ensure the child’s information is easy to
            access for supervisors, parents, or case managers.
          </li>
          <li>
            Determine which files are necessary for supervisors or guardians to
            have access to, protecting sensitive information as needed.
          </li>
        </ul>
        <Subheading className="mt-4">Tip for Child-Related Files:</Subheading>
        Sharing relevant medical or educational updates with supervisors helps
        them better manage visitations while safeguarding the child's
        well-being. Keep unnecessary or sensitive files restricted to authorized
        parties only.
      </>
    ),
  };

  return (
    <>
      <Button plain onClick={handleAction}>
        <InformationCircleIcon className="w-5 h-5 mr-2" />
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>What are Files?</DialogTitle>
        <DialogBody className="text-gray-500 dark:text-gray-50">
          {bodyContent[type]}</DialogBody>
        <DialogActions>
          <Button plain onClick={() => setOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FilesLearnMore;
